import React, { useEffect, useState, useCallback } from "react";
import { FaFileInvoiceDollar, FaDownload, FaQrcode, FaCheckCircle, FaClock, FaTimesCircle, FaCopy } from "react-icons/fa";
import api from "../api";
import { theme, t } from "../theme";
import { entregarArquivo } from '../platform/arquivos';
import { isNative } from '../platform/native';
import { podeColetarPagamento } from '../platform/cobranca';
import * as lojaBilling from '../platform/billing';

// Guideline 3.1.1: dentro do app nao se cobra por fora da loja — e PIX e uma
// forma de cobranca como qualquer outra. O recibo continua: baixar um PDF do que
// ja foi pago nao e transacao.
const COBRANCA_DIRETA = () => podeColetarPagamento({
  nativo: isNative(),
  lojaDisponivel: lojaBilling.disponivel(),
});

const fmtMoney = (n) => `R$ ${Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('pt-BR');
};

const STATUS_CFG = {
  paid:     { label: 'Paga',     color: '#16a34a', icon: FaCheckCircle },
  pending:  { label: 'Pendente', color: '#d97706', icon: FaClock },
  failed:   { label: 'Atrasada', color: '#e11d48', icon: FaTimesCircle },
  canceled: { label: 'Cancelada', color: '#64748b', icon: FaTimesCircle },
};

const FaturasHistorico = () => {
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState(null);
  const [pixOpen, setPixOpen] = useState(null); // {qr_code, qr_code_url, expires_at, charge_id}
  const [pixLoading, setPixLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setError(null);
    api.get('/billing/invoices')
      .then(res => setInvoices(res.data?.invoices || []))
      .catch(err => setError(err.response?.data?.erro || 'Falha ao carregar faturas'));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Polling: quando modal PIX aberto, reverifica a fatura a cada 8s
  useEffect(() => {
    if (!pixOpen) return;
    const i = setInterval(() => {
      api.get('/billing/invoices').then(res => {
        const list = res.data?.invoices || [];
        const updated = list.find(inv => inv.charge_id === pixOpen.charge_id);
        if (updated && updated.status === 'paid') {
          setPixOpen(null);
          setInvoices(list);
        }
      }).catch(() => {});
    }, 8000);
    return () => clearInterval(i);
  }, [pixOpen]);

  const cobrancaDireta = COBRANCA_DIRETA();

  const handlePagar = async (invoice) => {
    // Trava no handler, nao so no botao: guideline 3.1.1 nao admite "o botao
    // nao aparecia" como defesa se o caminho existe.
    if (!cobrancaDireta) return;
    setPixLoading(true); setError(null);
    try {
      const res = await api.post(`/billing/invoices/${invoice.charge_id}/pix`);
      setPixOpen({
        ...res.data,
        original_charge_id: invoice.charge_id,
      });
    } catch (err) {
      setError(err.response?.data?.erro || 'Falha ao gerar PIX');
    } finally {
      setPixLoading(false);
    }
  };

  const handleBaixarRecibo = (charge_id) => {
    // Abrir em nova aba com header de Authorization. Como <a download> nao
    // suporta headers, faz fetch + blob.
    api.get(`/billing/invoices/${charge_id}/recibo.pdf`, { responseType: 'blob' })
      .then(res => {
        entregarArquivo(new Blob([res.data], { type: 'application/pdf' }),
          `recibo-metria-${charge_id.slice(0, 10)}.pdf`);
      })
      .catch(err => setError(err.response?.data?.erro || 'Falha ao baixar recibo'));
  };

  const copyQR = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  if (invoices === null) return <div style={{ padding: 16, color: theme.colors.textMuted }}>Carregando faturas...</div>;

  return (
    <section style={styles.section}>
      <h3 style={styles.title}>
        <FaFileInvoiceDollar style={{ marginRight: 8, color: theme.colors.primary }} />
        Histórico de faturas
      </h3>

      {error && <div style={styles.error}>{error}</div>}

      {invoices.length === 0 ? (
        <div style={styles.empty}>Você ainda não tem faturas. As cobranças aparecem aqui após a primeira mensalidade.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Data</th>
                <th style={styles.th}>Valor</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Método</th>
                <th style={styles.th}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const cfg = STATUS_CFG[inv.status] || { label: inv.status, color: '#64748b', icon: FaClock };
                const Icon = cfg.icon;
                const date = inv.paid_at || inv.due_at || inv.cycle_start;
                return (
                  <tr key={inv.id || inv.charge_id}>
                    <td style={styles.td}>{fmtDate(date)}</td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{fmtMoney(inv.amount)}</td>
                    <td style={styles.td}>
                      <span style={{ color: cfg.color, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icon size={12} />
                        {cfg.label}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {inv.payment_method ? inv.payment_method.replace('_', ' ') : '-'}
                    </td>
                    <td style={{ ...styles.td, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {inv.can_download_recibo && (
                        <button onClick={() => handleBaixarRecibo(inv.charge_id)} style={{ ...t.btnSecondary, padding: '6px 10px', fontSize: 12 }}>
                          <FaDownload size={11} style={{ marginRight: 4 }} />
                          Recibo
                        </button>
                      )}
                      {inv.can_pay_pix && cobrancaDireta && (
                        <button onClick={() => handlePagar(inv)} disabled={pixLoading} style={{ ...t.btnPrimary, padding: '6px 10px', fontSize: 12 }}>
                          <FaQrcode size={11} style={{ marginRight: 4 }} />
                          {pixLoading ? 'Gerando...' : 'Pagar via PIX'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pixOpen && (
        <div style={styles.overlay} onClick={() => setPixOpen(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, fontSize: 18, color: theme.colors.text }}>
              <FaQrcode style={{ marginRight: 8, color: theme.colors.primary }} />
              Pagar via PIX
            </h3>
            <p style={{ fontSize: 14, color: theme.colors.textSecondary, marginBottom: 16 }}>
              Valor: <strong>{fmtMoney(pixOpen.amount)}</strong>
              {pixOpen.expires_at && <> · expira em {fmtDate(pixOpen.expires_at)}</>}
            </p>

            {pixOpen.qr_code_url && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <img src={pixOpen.qr_code_url} alt="QR Code PIX" style={{ maxWidth: 260, border: `1px solid ${theme.colors.borderLight}`, borderRadius: 8, padding: 8 }} />
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>
                Ou copie e cole no app do seu banco:
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  readOnly
                  value={pixOpen.qr_code || ''}
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, fontSize: 11, fontFamily: 'monospace' }}
                  onFocus={(e) => e.target.select()}
                />
                <button onClick={() => copyQR(pixOpen.qr_code)} style={t.btnSecondary}>
                  <FaCopy size={12} style={{ marginRight: 4 }} />
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
            </div>

            <div style={{ background: '#FEF3C7', padding: '10px 14px', borderRadius: 8, fontSize: 13, color: '#92400E', marginBottom: 16 }}>
              ⏳ Após pagar, esta tela atualiza automaticamente em alguns segundos.
            </div>

            <button onClick={() => setPixOpen(null)} style={{ ...t.btnSecondary, width: '100%' }}>
              Fechar
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const styles = {
  section: {
    background: '#fff', padding: 20, borderRadius: 12,
    border: `1px solid ${theme.colors.borderLight || '#EDE5E0'}`,
    marginBottom: 24,
  },
  title: {
    marginTop: 0, marginBottom: 16, fontSize: 16, fontWeight: 700,
    display: 'flex', alignItems: 'center', color: theme.colors.text,
  },
  empty: {
    padding: 24, textAlign: 'center', color: theme.colors.textMuted,
    fontSize: 14, background: theme.colors.bgSection, borderRadius: 8,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${theme.colors.borderLight}`,
    fontSize: 11, fontWeight: 600, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5,
  },
  td: { padding: '12px', borderBottom: `1px solid ${theme.colors.borderLight}`, color: theme.colors.text },
  error: {
    background: '#FEE', color: '#B91C1C', padding: '10px 12px',
    borderRadius: 8, fontSize: 13, marginBottom: 12,
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16, backdropFilter: 'blur(2px)',
  },
  modal: {
    background: '#fff', borderRadius: 16, padding: 24,
    maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
  },
};

export default FaturasHistorico;
